import React, { useEffect } from "react";
import type { AppProps } from "next/app";
import Head from "next/head";
import { LazyMotion, domMax } from "framer-motion";
import { Provider } from "react-wrap-balancer";
import { isProduction } from "lib/utils";

import ErrorBoundary from "components/errorBoundary/errorBoundary";
import AppContent from "components/app/AppContent";
import { BannerProvider } from "lib/context/bannerContext";

import "../styles/globals.css";
import "../styles/tailwind.css";
import "../styles/zephr.scss";

import {
  recklessNeueFontFamily,
  aktivGroteskFontFamily,
  sourceSansFontFamily,
} from "styles/fonts";

import { Mixpanel, setMixpanelOnPageLoad } from "lib/mixpanel/mixpanel";
import { MIXPANEL_TOKEN } from "lib/constants";

import { StoreProvider } from "lib/store/storeProvider";
import { useHydrate } from "lib/store";
import { initDatadogLogger } from "lib/datadog/logger";
import { useCookiebot } from "lib/hooks/useCookiebot";
import { useUserData } from "lib/store/user";
import { ZephrTracker } from "components/zephr/ZephrTracker";
import { TagManager } from "components/thirdPartyScripts/tagManager";

let mixpanel: Mixpanel | null = null;

function MyApp({ Component, pageProps }: AppProps) {
  const { userId, readerSubscriptionLevel, hasMissingInfo, showVerification } =
    useUserData();

  const store = useHydrate({
    ...pageProps.__initialState,
    user: {
      userId,
      readerSubscriptionLevel,
      hasMissingInfo,
      showVerification,
    },
  });

  useEffect(() => {
    const ayncFn = async () => {
      mixpanel = new Mixpanel(MIXPANEL_TOKEN);
      const id = userId;

      setMixpanelOnPageLoad();

      if (id && !window.location.pathname.includes("/articles/")) {
        let additionalTrackingProperties = {};
        if (
          pageProps &&
          pageProps.trackingData &&
          pageProps.trackingData.mixpanel
        ) {
          additionalTrackingProperties = pageProps.trackingData.mixpanel;
        }

        mixpanel.track("Page View", {
          ...additionalTrackingProperties,
          "Page Path": window.location.pathname,
          distinct_id: id,
        });
      }
    };

    ayncFn();

    initDatadogLogger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCookiebot();

  return (
    <ZephrTracker token={MIXPANEL_TOKEN}>
      {/* afterInteractive scripts live here rather than _document, which never
          hydrates and so would never run their injection effect. Cookiebot is
          beforeInteractive and so stays in _document. */}
      <TagManager />
      <Head>
        {!isProduction && <meta name="robots" content="noindex,nofollow" />}
      </Head>
      <style jsx global>{`
        :root {
          --font-recklessNeueBold: ${recklessNeueFontFamily.style.fontFamily};
          --font-aktivGrotesk: ${aktivGroteskFontFamily.style.fontFamily};
          --font-sourceSans: ${sourceSansFontFamily.style.fontFamily};
        }
      `}</style>
      <ErrorBoundary fullSizeError={true}>
        <LazyMotion features={domMax} strict>
          <StoreProvider store={store}>
            <BannerProvider>
              <Provider>
                <AppContent Component={Component} pageProps={pageProps} />
              </Provider>
            </BannerProvider>
          </StoreProvider>
        </LazyMotion>
      </ErrorBoundary>
    </ZephrTracker>
  );
}

export default MyApp;
